import { faInfoCircle, faPlay, faVideo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { Button, Card, Container, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAPI } from '../../../contexts/APIContext';
import { AppShell } from '../../components/AppShell/AppShell';
import { Footer } from '../../components/Footer/Footer';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import { Title } from '../../components/Title/Title';
import { LiveUtils } from '../../utils/utils';
import styles from './Live.module.scss';

function safePromise<T>(promise: Promise<T>, defaultValue: T): Promise<T> {
  return promise.catch((error) => {
    console.error(error);
    return defaultValue;
  });
}

const LiveComponent = () => {
  const { t } = useTranslation();
  const { getMyLives, getMyPastLives, getPastLiveFileUrl, joinLive, accessRecording } = useAPI();
  const [loading, setLoading] = useState(true);
  const [lives, setLives] = useState<any[]>([]);
  const [pastLives, setPastLives] = useState<any[]>([]);
  const [selectedLive, setSelectedLive] = useState<any>();
  const [accessLive, setAccessLive] = useState<any>();

  const accessStartDate = (participation: any) => new Date(participation.accessStartsAt);

  const join = (participation: any) => {
    if (Date.now() < accessStartDate(participation).getTime()) {
      setAccessLive(participation);
      return;
    }
    joinLive(participation.live.id);
  };

  useEffect(() => {
    Promise.all([safePromise(getMyLives(), []), safePromise(getMyPastLives(), [])])
      .then(([lives, pastLives]) => {
        setLives(lives);
        setPastLives(pastLives);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <FullscreenLoader />;

  return (
    <AppShell>
      <div className={`${styles.page} profile-page`}>
        <Title>{t('live.labels.title')}</Title>

        <main className={styles.content}>
          <h2>{t('live.labels.upcomingLives')}</h2>
          {lives.length > 0 ? (
            <Container className={styles.responsiveGrid}>
              {lives.map((p) => (
                <Card className={`${styles.card} surface-card`} key={p.id}>
                  <Card.Img
                    variant="top"
                    className={`${styles.cardImg} ${!p.live?.imageUrl ? styles.cardImgDefault : ''}`}
                    src={p.live?.imageUrl ?? `${process.env.PUBLIC_URL + '/live.svg'}`}
                  />
                  <Card.Body>
                    <Card.Title>{p.live.title}</Card.Title>
                    <div>
                      {LiveUtils.dateFormatter.format(new Date(p.live.startDate))} {LiveUtils.timeFormatter.format(new Date(p.live.startDate))}
                    </div>
                    <div>
                      {LiveUtils.dateFormatter.format(new Date(p.live.endDate))} {LiveUtils.timeFormatter.format(new Date(p.live.endDate))}
                    </div>
                    {/* <div style={{ whiteSpace: 'normal' }}>{p.live.description}</div> */}
                  </Card.Body>
                  <Card.Footer className={'d-flex justify-content-end'}>
                    {!!p.live.description && (
                      <Button variant={'secondary'} className="tootor-button tootor-button--basic" onClick={() => setSelectedLive(p.live)} aria-label="Info">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2" /> {t('live.labels.info')}
                      </Button>
                    )}
                    <Button disabled={loading} variant={'primary'} className="tootor-button tootor-button--primary ml-auto" onClick={() => join(p)}>
                      <FontAwesomeIcon icon={faPlay} className="mr-2" /> {t('live.labels.join')}
                    </Button>
                  </Card.Footer>
                </Card>
              ))}
            </Container>
          ) : (
            <>
              <div>{t('live.labels.noUpcomingLives')}</div>
            </>
          )}

          {pastLives.length > 0 && (
            <>
              <h2 className={'mt-4'}>{t('live.labels.pastLives')}</h2>
              <Container className={styles.responsiveGrid}>
                {pastLives.map((p) => (
                  <Card className={`${styles.card} surface-card`} key={p.id}>
                    <Card.Img
                      variant="top"
                      className={`${styles.cardImg} ${!p.live?.imageUrl ? styles.cardImgDefault : ''}`}
                      src={p.live?.imageUrl ?? `${process.env.PUBLIC_URL + '/live.svg'}`}
                    />
                    <Card.Body>
                      <Card.Title>{p.live.title}</Card.Title>
                      <div>{LiveUtils.dateFormatter.format(new Date(p.live.startDate))}</div>
                      {p.live.liveFiles?.length > 0 && (
                        <>
                          <div className={'mt-3'}>
                            <strong>{t('live.labels.materials')}</strong>
                          </div>
                          <ul style={{ marginLeft: '1rem' }}>
                            {p.live.liveFiles.map((file) => (
                              <li key={file.id}>
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    getPastLiveFileUrl(p.live.id, file.id);
                                  }}
                                >
                                  {file.name}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </Card.Body>
                    {(!!p.live.description || p.live.recordings?.length > 0) && (
                      <Card.Footer className={'d-flex justify-content-end align-items-start'}>
                        {!!p.live.description ? (
                          <Button
                            variant={'secondary'}
                            className="tootor-button tootor-button--basic mr-auto"
                            onClick={() => setSelectedLive(p.live)}
                            aria-label="Info"
                          >
                            <FontAwesomeIcon icon={faInfoCircle} className="mr-2" /> {t('live.labels.info')}
                          </Button>
                        ) : (
                          <div className={'mr-auto'} />
                        )}
                        {p.live.recordings?.length > 0 && (
                          <div className={'d-flex flex-column ml-auto'} style={{ gap: '8px' }}>
                            {p.live.recordings.map((recording, i) => (
                              <Button
                                key={recording.id}
                                variant={'primary'}
                                className="tootor-button tootor-button--primary"
                                onClick={() => accessRecording(p.live.id, recording.id)}
                              >
                                <FontAwesomeIcon icon={faVideo} className="mr-2" /> {t('live.labels.watchRecording')}
                                {p.live.recordings.length > 1 && <> {i + 1}</>}
                              </Button>
                            ))}
                          </div>
                        )}
                      </Card.Footer>
                    )}
                  </Card>
                ))}
              </Container>
            </>
          )}
        </main>

        <Modal show={!!selectedLive} onHide={() => setSelectedLive(undefined)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{selectedLive?.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{selectedLive?.description}</Modal.Body>
        </Modal>

        <Modal show={!!accessLive} onHide={() => setAccessLive(undefined)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{accessLive?.live.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {accessLive &&
              t('live.labels.accessFrom') +
                `${LiveUtils.dateFormatter.format(accessStartDate(accessLive))} ${LiveUtils.timeFormatter.format(accessStartDate(accessLive))}.`}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setAccessLive(undefined)}>
              {t('labels.chiudi')}
            </Button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>
    </AppShell>
  );
};

export { LiveComponent as Live };
